import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Page} from "../model/paged-response";
import {PageSpec} from "../model/search/page-spec";

export type LoadMode = "replace" | "append";

export type LazyTrigger<TArgs, TResult> = (args: TArgs) => {
  unwrap: () => Promise<TResult>;
};

export type WithPage<TArgs> = TArgs & { page: PageSpec };

export interface UsePagedRtkQueryOptions<TArgs, TDto, TItem, TId> {
  args: TArgs;
  pageSize: number;
  trigger: LazyTrigger<WithPage<TArgs>, Page<TDto>>;
  getId: (item: TItem) => TId;
  mapItem?: (dto: TDto) => TItem;
  resetKey?: any;
  enabled?: boolean;
  initialPage?: number;
  withPage?: (args: TArgs, page: PageSpec) => WithPage<TArgs>;
}

export interface UsePagedRtkQueryResult<TItem> {
  items: TItem[];
  total: number;
  page: number;
  pageSize: number;

  hasMore: boolean;
  canLoadMore: boolean;

  isFetching: boolean;
  isLoadingFirstPage: boolean;
  error: unknown;

  refresh: () => void;
  loadMore: () => void;
  loadPage: (page: number, mode?: LoadMode) => void;
  reset: () => void;
}

export const usePagedRtkQuery = <TArgs, TDto, TItem = TDto, TId = unknown>(
  opts: UsePagedRtkQueryOptions<TArgs, TDto, TItem, TId>
): UsePagedRtkQueryResult<TItem> => {
  const {
    args,
    pageSize,
    trigger,
    mapItem,
    getId,
    resetKey,
    enabled = true,
    initialPage = 0,
    withPage,
  } = opts;

  const [items, setItems] = useState<TItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  // Prevent out-of-order responses from stomping newer state
  const requestSeq = useRef(0);

  const buildArgs = useCallback(
    (nextPage: number): WithPage<TArgs> => {
      const p = { page: nextPage, size: pageSize };
      return withPage ? withPage(args, p) : ({ ...args, page: p } as WithPage<TArgs>);
    },
    [args, pageSize, withPage]
  );

  const hasMore = items.length < total;
  const canLoadMore = enabled && !isFetching && hasMore;

  const loadPage = useCallback(
    async (nextPage: number, mode: LoadMode = "replace") => {
      if (!enabled) return;

      const mySeq = ++requestSeq.current;
      setIsFetching(true);
      setError(undefined);

      try {
        const endpointArgs = buildArgs(nextPage);
        const res = await trigger(endpointArgs).unwrap();

        const rawItems = res.content;
        const itemMapper = mapItem ?? ((x: any) => x);
        const mappedItems = rawItems.map(itemMapper);

        // If a newer request started since this one, ignore this response
        if (mySeq !== requestSeq.current) return;

        setTotal(res.totalElements);

        if (mode === "replace") {
          setItems(mappedItems);
          setPage(nextPage);
          return;
        }

        // append with de-dupe
        setItems(prev => {
          const seen = new Set<TId>(prev.map(getId));
          const merged = [...prev];
          for (const it of mappedItems) {
            const id = getId(it);
            if (!seen.has(id)) {
              seen.add(id);
              merged.push(it);
            }
          }
          return merged;
        });
        setPage(nextPage);
      } catch (e) {
        if (mySeq === requestSeq.current) {
          setError(e);
        }
      } finally {
        if (mySeq === requestSeq.current) {
          setIsFetching(false);
        }
      }
    },
    [enabled, buildArgs, trigger, mapItem, getId]
  );

  const refresh = useCallback(() => {
    loadPage(initialPage, "replace");
  }, [loadPage, initialPage]);

  const loadMore = useCallback(() => {
    if (!canLoadMore) {
      return;
    }
    loadPage(page + 1, "append");
  }, [canLoadMore, loadPage, page]);

  const reset = useCallback(() => {
    // bump seq so any in-flight responses are ignored
    requestSeq.current++;
    setItems([]);
    setTotal(0);
    setPage(initialPage);
    setIsFetching(false);
    setError(undefined);
  }, [initialPage]);

  // Auto refresh when resetKey changes (filters changed)
  useEffect(() => {
    if (!enabled) return;
    // On first mount, also loads page 0
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, enabled]);

  const isLoadingFirstPage = isFetching && items.length === 0;

  return useMemo(
    () => ({
      items,
      total,
      page,
      pageSize,
      hasMore,
      isFetching,
      isLoadingFirstPage,
      error,
      refresh,
      loadMore,
      loadPage,
      reset,
      canLoadMore,
    }),
    [
      items,
      total,
      page,
      pageSize,
      hasMore,
      isFetching,
      isLoadingFirstPage,
      error,
      refresh,
      loadMore,
      loadPage,
      reset,
      canLoadMore,
    ]
  );
}
